if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};

      /** Grid mode: rendered as a CSS grid, Swiper must not initialise */
      this.isGrid = this.hasAttribute('data-grid-mode');
      this.wrapperSelector = this.isGrid
        ? '.card-product-grid__wrapper'
        : '.swiper-wrapper';

      this.sliderWrapper = this.querySelector(this.wrapperSelector);
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');
      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',
        resistanceRatio: 0.72,
        breakpoints: {
          750: {
            slidesPerView: swiperOptions.slidesPerViewTablet || 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };
      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }
      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }
      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }
      if (this.isGrid) {
        this.initCards();
        return;
      }
      this.initSlider();
      this.syncHeights();
      window.addEventListener("resize", () => this.syncHeights());
    }

    syncHeights() {
      const textSlide = this.querySelector('.products-feed__card-text-custom-slide');
      const actionElement = this.querySelector('.product-card__actions');

      if (textSlide && actionElement) {
        const actionHeight = actionElement.offsetHeight;
        textSlide.style.height = `${actionHeight}px`;
      }
    }

    /** Complete-the-look: top up with a fallback feed until data-max-items, no duplicates */
    fetchWithFallback() {
      const parse = text => new DOMParser().parseFromString(text, 'text/html');
      const pick = html =>
        html.querySelector(this.wrapperSelector) ||
        html.querySelector('.swiper-wrapper') ||
        html.querySelector('.card-product-grid__wrapper');
      const keyOf = el => {
        const a = el.querySelector('a[href*="/products/"]');
        if (!a) return null;
        const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
        return m ? m[1] : null;
      };
      const max = parseInt(this.getAttribute('data-max-items'), 10) || 4;
      const exclude = this.getAttribute('data-exclude-product');
      const get = url => fetch(url).then(r => r.text()).then(parse).then(pick).catch(() => null);
      return get(this.getAttribute('data-url')).then(first => {
        const seen = new Set(exclude ? [exclude] : []);
        const items = [];
        const add = wrapper => {
          if (!wrapper) return;
          Array.from(wrapper.children).forEach(el => {
            if (items.length >= max) return;
            const k = keyOf(el);
            if (!k || seen.has(k)) return;
            seen.add(k);
            items.push(el);
          });
        };
        add(first);
        const done = () => {
          const doc = document.implementation.createHTMLDocument('');
          const w = doc.createElement('div');
          w.className = (first && first.className) || '';
          items.forEach(el => w.appendChild(el));
          return { querySelector: () => w };
        };
        if (items.length >= max) return done();
        return get(this.getAttribute('data-fallback-url')).then(second => {
          add(second);
          return done();
        });
      });
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');
      const source = this.hasAttribute('data-fallback-url')
        ? this.fetchWithFallback()
        : fetch(this.getAttribute('data-url'))
            .then(response => response.text())
            .then(text => new DOMParser().parseFromString(text, 'text/html'));
      source
        .then(html => {
          /** Fall back to the other wrapper class so a mismatch never blanks the section */
          const wrapper =
            html.querySelector(this.wrapperSelector) ||
            html.querySelector('.swiper-wrapper') ||
            html.querySelector('.card-product-grid__wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            const target =
              this.querySelector(this.wrapperSelector) ||
              this.querySelector('.swiper-wrapper') ||
              this.querySelector('.card-product-grid__wrapper');

            if (!target) return;

            target.innerHTML = wrapper.innerHTML;
            this.parent.classList.remove('hidden');

            if (this.isGrid) {
              this.initCards();
              return;
            }
            this.initSlider();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    /** Card initialisation shared by slider and grid mode */
    initCards() {
      if (
        this.hasAttribute('data-init-quick-cart') &&
        document.querySelector('quick-cart-drawer') &&
        this.querySelector('.swiper-slide, .card-product-grid__item')
      ) {
        document.querySelector('quick-cart-drawer').init();
      }

      this.querySelectorAll('product-card').forEach(card => {
        card.init();
      });
    }

    initSlider(slider = this) {
      this.slider = new Swiper(slider, this.sliderOptions);

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;
      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });

      if (this.swiperOptions?.navigation) {
        const sliderButtons = [
          this.slider.navigation.prevEl,
          this.slider.navigation.nextEl
        ];

        sliderButtons.forEach((button, i) => {
          let type = 'Previous';

          if (i === 1) {
            type = 'Next';
          }

          button.setAttribute('aria-label', `${type} slide`);
          button.setAttribute(
            'aria-controls',
            this.slider.wrapperEl.id
          );
        });
      }

      this.slider.wrapperEl.addEventListener('focusin', e => {
        this.slider.el.scrollLeft = 0;

        const slide = e.target.closest('.swiper-slide');
        const slideIndex = +slide.dataset.index;
        const isFirstSlide = slideIndex === 0;
        const isLastSlide =
          slideIndex === this.slider.slides.length - 1;

        if (isFirstSlide || isLastSlide) {
          this.slider.slideTo(slideIndex, 0);

          return;
        }

        const slideRect = slide.getBoundingClientRect();
        const wrapperWidth = this.slider.wrapperEl.clientWidth;
        // prettier-ignore
        const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);
        const isSlideVisible =
          slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

        if (isSlideVisible) {
          return;
        }

        slideLeft >= slideRect.width
          ? this.slider.slideNext(0)
          : this.slider.slidePrev(0);
      });

      const swiperOptions = JSON.parse(this.getAttribute('data-swiper-options')) || {};
      /** stop / start autoplay on hover if quick cart is not open */
      this.slider.wrapperEl.addEventListener('mouseenter', () =>
        this.slider.autoplay.stop()
      );
      this.slider.wrapperEl.addEventListener('mouseleave', () => {
        if (!this.classList.contains('product--open-on-quick-cart') && this.sliderOptions.autoplay) {
          this.slider.autoplay.start();
        }
        if (swiperOptions.autoplay === false) {
          this.slider.autoplay.stop();
        }
      });

      this.initCards();

      if (Shopify.designMode) {
        this.initCards();
      }
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}
